import React, { memo, useCallback, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useNotifications } from '../contexts/NotificationContext';
import Icon from './Icon';
import BrandLogo from './BrandLogo';
import { getGuestNavItems, getNavItemsForRole } from '../utils/navigationUtils';
import { getUserInitials as getInitials, getUserDisplayName, getPremiumBadge } from '../utils/userDisplay';
import { getLayoutMode } from '../routing/layoutModes';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

const MobileLayout: React.FC<{ children: React.ReactNode }> = memo(({ children }) => {

  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { getThemeStyles } = useTheme();
  const themeStyles = getThemeStyles();
  const { unreadCount: notifUnreadCount } = useNotifications();
  const [menuOpen, setMenuOpen] = useState(false);
  const layoutMode = getLayoutMode(location.pathname);
  const shouldLockBodyScroll = layoutMode !== 'auth' && layoutMode !== 'full-bleed' && menuOpen;

  useBodyScrollLock(shouldLockBodyScroll);

  if (layoutMode === 'auth' || layoutMode === 'full-bleed') {
    return <>{children}</>;
  }

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  const handleMenuToggle = () => setMenuOpen((v) => !v);

  const menuItems = user ? getNavItemsForRole(user.role) : getGuestNavItems();

     return (
    <div
      className="mobile-layout theme-shell animate-fade-in"
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        background: themeStyles.backgroundGradient,
        color: themeStyles.text,
        position: 'relative',
        overflowX: 'hidden',
        overflowY: 'clip'
      }}
    >
  <div className="layout-glow layout-glow--indigo animate-float" aria-hidden="true" />
  <div className="layout-glow layout-glow--cyan animate-float" aria-hidden="true" />
      {/* Header Mobile Premium */}
      <header
        className="header-mobile safe-area-top animate-fade-in"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1001,
          background: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(16px) saturate(170%)',
          WebkitBackdropFilter: 'blur(16px) saturate(170%)',
          borderBottom: `1px solid rgba(2,6,23,0.08)`,
          boxShadow: '0 10px 24px rgba(15,23,42,0.1)',
          padding: '0.85rem 1.25rem',
          minHeight: 'var(--header-height-mobile)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem'
        }}
      >
  {/* Logo Grented */}
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
    <BrandLogo height={72} />
    <span style={{
      marginTop: '-4px',
      fontSize: '10px',
      color: '#0f766e',
      fontWeight: 700,
      letterSpacing: '0.03em',
      textTransform: 'uppercase'
    }}>
      trusted tenant intelligence
    </span>
  </div>

        <button
          aria-label="Apri menu"
          onClick={handleMenuToggle}
          style={{
            width: 42,
            height: 42,
            borderRadius: 12,
            border: `1px solid ${themeStyles.border}`,
            background: '#ffffff',
            color: '#0f172a',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 18px rgba(15,23,42,0.08)'
          }}
        >
          <Icon name="hamburger" size={20} />
        </button>
      </header>

      {/* Overlay scuro per chiusura menu */}
      {menuOpen && (
        <div
          onClick={() => setMenuOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100dvh',
            background: 'rgba(4, 7, 21, 0.65)',
            zIndex: 1199,
            transition: 'opacity 0.3s ease',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)'
          }}
        />
      )}

      {/* Side menu mobile premium */}
      <nav
        className="side-menu-mobile"
        style={{
          position: 'fixed',
          top: 0,
          right: menuOpen ? 0 : '-88vw',
          width: '86vw',
          maxWidth: 340,
          height: '100dvh',
          maxHeight: '100dvh',
          background: 'rgba(255,255,255,0.94)',
          boxShadow: menuOpen ? themeStyles.cardShadow : 'none',
          zIndex: 1200,
          transition: 'right 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          borderTopLeftRadius: 24,
          borderBottomLeftRadius: 24,
          pointerEvents: menuOpen ? 'auto' : 'none',
          border: `1px solid ${themeStyles.border}`,
          backdropFilter: 'blur(26px) saturate(165%)',
          WebkitBackdropFilter: 'blur(26px) saturate(165%)',
          display: 'flex',
          flexDirection: 'column'
        }}
        {...(!menuOpen ? { inert: 'true' } : {})}
      >
        {/* Header del menu */}
        <div
        style={{
          padding: '1.25rem 1.25rem 1.5rem',
          borderBottom: `1px solid ${themeStyles.border}`,
          background: 'linear-gradient(130deg, #0f766e 0%, #0ea5e9 100%)',
          borderTopLeftRadius: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.75rem'
        }}>
          {user ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.9rem',
              minWidth: 0
            }}>
              <button
                onClick={() => {
                  navigate('/profile');
                  setMenuOpen(false);
                }}
                style={{
                  width: '46px',
                  height: '46px',
                  borderRadius: '20px',
                  background: 'rgba(255, 255, 255, 0.22)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.95rem',
                  fontWeight: '700',
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  boxShadow: '0 8px 22px -10px rgba(255, 255, 255, 0.35)',
                  cursor: 'pointer'
                }}
              >
                {getInitials(user, user)}
              </button>
              <div style={{ minWidth: 0 }}>
                <div style={{
                  color: 'white',
                  fontWeight: '700',
                  fontSize: '1.02rem',
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {getUserDisplayName(user, user)} {getPremiumBadge(user)}
                </div>
                <div style={{
                  color: 'rgba(255, 255, 255, 0.82)',
                  fontSize: '0.82rem',
                  textTransform: 'capitalize'
                }}>
                  {user.role}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>Benvenuto su Grented</div>
              <div style={{ color: 'rgba(255,255,255,0.82)', fontSize: '0.82rem' }}>Apri il menu per accedere a tutte le sezioni</div>
            </div>
          )}

          <button
            aria-label="Chiudi menu"
            onClick={() => setMenuOpen(false)}
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.4)',
              background: 'rgba(255,255,255,0.18)',
              color: '#fff',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <span style={{ fontSize: 20, lineHeight: 1 }}>×</span>
          </button>
        </div>

        {!user && (
          <div style={{
            padding: '0.9rem 1rem',
            borderBottom: `1px solid ${themeStyles.border}`
          }}>
            <Link
              to="/login"
              onClick={() => setMenuOpen(false)}
              style={{
                width: '100%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.55rem',
                padding: '0.9rem 1rem',
                borderRadius: 14,
                border: '1px solid rgba(2,6,23,0.08)',
                background: 'linear-gradient(130deg, #0f766e 0%, #0ea5e9 100%)',
                color: '#fff',
                fontWeight: 700,
                textDecoration: 'none'
              }}
            >
              <Icon name="user" size={18} />
              <span>Accedi / Registrati</span>
            </Link>
          </div>
        )}

        {/* Logout */}
        {/* Navigation items - completi in hamburger */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '1rem',
          WebkitOverflowScrolling: 'touch', // Smooth scrolling su iOS
          scrollbarWidth: 'none', // Nasconde scrollbar Firefox
          msOverflowStyle: 'none' // Nasconde scrollbar IE/Edge
        }}
        className="mobile-menu-scroll">
          {menuItems.map((item, index) => (
            <button
              key={index}
              onClick={() => {
                navigate(item.path);
                setMenuOpen(false);
              }}
              style={{
                width: '90%',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1rem',
                margin: '.4rem 1rem',
                background: 'rgba(255, 255, 255, 0.08)',
                border: `1px solid ${themeStyles.border}`,
                borderRadius: '24px',
                color: themeStyles.text,
                fontSize: '1.3rem',
                fontWeight: '450',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textAlign: 'left',
                boxShadow: '0 12px 32px -20px rgba(6, 182, 212, 0.45)'
              }}
              onTouchStart={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.16)';
                e.currentTarget.style.transform = 'translateX(4px)';
              }}
              onTouchEnd={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              <span style={{ fontSize: '1.5rem', display: 'inline-flex', alignItems: 'center' }}><Icon name={item.icon} size={24} /></span>
              <span style={{ flex: 1 }}>{item.label}</span>
              {item.path === '/notifiche' && notifUnreadCount > 0 && (
                <span style={{
                  minWidth: 22,
                  height: 22,
                  borderRadius: 999,
                  background: '#ef4444',
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 6px'
                }}>
                  {notifUnreadCount > 99 ? '99+' : notifUnreadCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {user && (
          <div style={{
            padding: '1rem',
            borderTop: `1px solid ${themeStyles.border}`
          }}>
            <button
              onClick={() => {
                handleLogout();
                setMenuOpen(false);
              }}
              style={{
                width: '90%',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1rem',
                margin: '0.4rem 1rem',
                background: 'linear-gradient(135deg, #f87171 0%, #ef4444 45%, #b91c1c 100%)',
                border: '1px solid rgba(248, 113, 113, 0.45)',
                borderRadius: '24px',
                color: 'white',
                fontSize: '1.2rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 16px 32px -18px rgba(248, 113, 113, 0.65)'
              }}
            >
              <Icon name="logout" size={24} />
              <span>Logout</span>
            </button>
          </div>
        )}

        {/* Menu chiuso automaticamente */}
      </nav>

      {/* Main Content */}
      <main
        className="main-content scrollable"
        style={{
          flex: 1,
          paddingTop: 'calc(var(--header-height-mobile) + 1rem)',
          paddingBottom: 24,
          paddingLeft: '1.25rem',
          paddingRight: '1.25rem',
          background: 'transparent',
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'center'
        }}
      >
        <div
          className="container glass-card animate-fade-in-up"
          style={{
            width: '100%',
            maxWidth: 960,
            background: themeStyles.cardGlass,
            border: `1px solid ${themeStyles.border}`,
            boxShadow: themeStyles.cardShadow,
            padding: '1.5rem',
            borderRadius: '28px',
            backdropFilter: 'blur(24px) saturate(165%)',
            WebkitBackdropFilter: 'blur(24px) saturate(165%)'
          }}
        >
          {children}
        </div>
      </main>

      {/* Inline styles for mobile layout */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .mobile-layout { 
            position: relative; 
            min-height: 100dvh;
            display: flex;
            flex-direction: column;
          }
          .main-content { 
            padding-left: 0; 
            padding-right: 0; 
          }
          @media (min-width: 768px) {
            .main-content { 
              padding-left: 1rem; 
              padding-right: 1rem; 
            }
          }
          .touch-target {
            min-height: 44px;
            min-width: 44px;
          }
          /* Safe area support for notched devices */
          .safe-area-top {
            padding-top: env(safe-area-inset-top);
          }
          .safe-area-bottom {
            padding-bottom: env(safe-area-inset-bottom);
          }
          /* Prevent text selection on touch */
          .nav-mobile-item {
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
            user-select: none;
            -webkit-touch-callout: none;
          }
          /* Hide scrollbar for mobile menu */
          .mobile-menu-scroll::-webkit-scrollbar {
            display: none;
          }
        `
      }} />
    </div>
  );
});

export default MobileLayout;
